"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { FrontendUserSession } from "./session";

/**
 * FrontendSessionContext — client-side dostęp do SSR-resolved session
 * (cookie-based — `imperial_frontend_session`).
 *
 * **Po co context:** server-side `getFrontendSession()` jest dostępne tylko
 * w Server Components. Client components (np. `<WUserAccount>` w headerze)
 * potrzebują tej samej info żeby wiedzieć czy user zalogowany — provider
 * w `PublicLayout` udostępnia ją bez prop drilling.
 *
 * **Wartość:** `null` gdy visitor nie zalogowany lub cookie wygasło.
 *
 * @example
 * ```tsx
 * "use client";
 * import { useFrontendSession } from "@/lib/api/session-context";
 *
 * function MyWidget() {
 *   const session = useFrontendSession();
 *   if (!session) return <span>Visitor</span>;
 *   return <span>Hi, {session.user.name}</span>;
 * }
 * ```
 */
const FrontendSessionContext = createContext<FrontendUserSession | null>(null);

export function FrontendSessionProvider({
  session,
  children,
}: {
  session: FrontendUserSession | null;
  children: ReactNode;
}) {
  return (
    <FrontendSessionContext.Provider value={session}>
      {children}
    </FrontendSessionContext.Provider>
  );
}

/**
 * Zwraca SSR-resolved frontend session dla bieżącego user'a (z cookie
 * `imperial_frontend_session`). Null gdy visitor lub cookie wygasło.
 */
export function useFrontendSession(): FrontendUserSession | null {
  return useContext(FrontendSessionContext);
}
